import { useEffect } from "react";
import Value from "aldoo-ra/Value";
import moment from "moment";
import { GetStatistics, GetAgents } from "./api";

// Refresh interval in seconds
const CS_REFRESH_STATISTICS = 3

/**
 * Custom hook for fetching and managing Customer Support statistics
 * Automatically polls the API and pauses when navigating away from CS pages
 * @returns {Object} Statistics data and control functions
 */
export const useCSStatistics = () => {
  const [statistics, setStatistics] = Value("cs-statistics", null);
  const [loading, setLoading] = Value("cs-statistics-loading", false);
  const [error, setError] = Value("cs-statistics-error", null);
  const [lastUpdated, setLastUpdated] = Value("cs-statistics-last-updated", null);

  // Function to fetch statistics data
  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await GetStatistics();

      if (response.result && response.data) {
        // Add a timestamp to force React to detect changes
        setStatistics({
          ...response.data,
          _lastFetch: Date.now()
        });
        setLastUpdated(moment().toISOString());
      } else {
        throw new Error(response.error || "Failed to fetch statistics");
      }
    } catch (err) {
      console.error("Error fetching support statistics:", err);
      setError(err.message || "Failed to fetch statistics");
    } finally {
      setLoading(false);
    }
  };

  // Setup polling for statistics updates
  useEffect(() => {
    // Fetch on component mount
    fetchStatistics();

    // Setup interval for repeated fetching
    const intervalId = setInterval(() => {
      // Check if we're still on the customer support path using window.location
      if (
        typeof window !== "undefined" &&
        window.location.href.includes("/admin/customer-support")
      ) {
        fetchStatistics();
      }
    }, CS_REFRESH_STATISTICS * 1000);

    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  return {
    statistics,
    loading,
    error,
    lastUpdated,
    refresh: fetchStatistics
  };
};

export default useCSStatistics;